import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Sparkles } from "lucide-react";
import Lymphie from "@/components/Lymphie";
import DonationProgress from "@/components/DonationProgress";
import DonateModal from "@/components/DonateModal";

export default function About() {
  const [showDonate, setShowDonate] = useState(false);

  return (
    <main className="min-h-screen py-12 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="about-back-link"
          className="inline-flex items-center gap-2 text-sm text-kawaii-textSoft hover:text-kawaii-text mb-8 font-body"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} />
          Back to my calendar
        </Link>

        <header className="mb-8 flex items-center gap-4">
          <Lymphie size={80} waving />
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-kawaii-textSoft font-bold">Meet the creator</p>
            <h1 className="font-heading text-4xl md:text-5xl text-kawaii-text mt-2">Hi, I'm Jen ♡</h1>
          </div>
        </header>

        <article className="kawaii-card p-8 md:p-10 space-y-6 font-body text-kawaii-text leading-relaxed">
          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">The story</h2>
            <p className="text-sm">
              I built MyLymphieLife because I live with lymphedema — the same condition it's designed to track.
              Every feature in this app started as something I wished existed on a hard day: the emergency script
              for that terrifying first cellulitis flare, the gentle mood tracking for the days that hurt in ways
              nobody talks about, the private space to just <em>write it down</em>.
            </p>
            <p className="text-sm mt-3">
              This isn't a big-team app or a VC-funded startup. It's one person, one condition, and a stubborn belief
              that fellow "Lymphies" deserve something soft and kind that respects our privacy.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">Why I'm fundraising</h2>
            <p className="text-sm">
              I need <strong>LVA surgery</strong> (lymphovenous anastomosis) — a specialised microsurgical procedure that
              connects lymph vessels directly to nearby veins so my body can drain properly again. It's one of the few
              treatments that meaningfully reduces limb size and cellulitis risk long-term.
            </p>
            <p className="text-sm mt-3">
              The catch: it's not routinely covered by public healthcare in my country and costs around{" "}
              <strong>$40,000 USD</strong> out of pocket, including specialist consultations, imaging, the surgery itself,
              and post-op compression garments.
            </p>
            <p className="text-sm mt-3">
              If MyLymphieLife has helped you — or if you'd like to help me get closer to walking around a little lighter —
              a donation of any size goes directly into the surgery fund. Every pound, dollar, and euro is tracked
              openly right here.
            </p>
          </section>

          <DonationProgress variant="full" onDonate={() => setShowDonate(true)} />

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">Where your money goes</h2>
            <ul className="text-sm list-disc pl-6 space-y-1.5">
              <li>100% of donations go to the surgery fund — nothing goes into the app.</li>
              <li>Stripe's processing fee (~2.9% + 30¢) is unavoidable but tiny.</li>
              <li>If the goal is exceeded, the surplus goes toward post-op physio and future medical costs — no lifestyle upgrades.</li>
              <li>If for any reason the surgery doesn't happen, I'll refund donors on request.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl text-kawaii-text mb-3">Say hi</h2>
            <p className="text-sm">
              Questions, encouragement, or just want to trade compression brand recommendations? Email me at{" "}
              <a href="mailto:hello@letscure.app" className="underline underline-offset-2">hello@letscure.app</a>.
              I read every message.
            </p>
          </section>

          <p className="text-xs text-kawaii-textSoft italic border-t border-kawaii-border pt-4">
            <Sparkles className="w-3 h-3 inline mr-1" strokeWidth={2.5} />
            Thank you for even reading this. Whether or not you donate — you're already part of what makes this community
            gentle and kind. ♡
          </p>
        </article>
      </div>

      {showDonate && <DonateModal onClose={() => setShowDonate(false)} />}
    </main>
  );
}
